import { API_BASE_URL } from "@kupikupi/brand";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addFavorite, deleteFavoritesBatch, queryFavoritePage } from "../endpoints/favorites";
import type { FavoritePageUrlInput } from "../path-adapter";
import type {
  AddFavoritesInput,
  DeleteFavoritesInput,
  FavoritePageResult,
} from "../types/favorites";

/** GET /favorites/page — list user's favorite SPUs (or shops). */
export function useFavoritePage(authToken: string | null, input: FavoritePageUrlInput = {}) {
  return useQuery<FavoritePageResult>({
    queryKey: ["favorites", "page", input],
    enabled: !!authToken,
    queryFn: () => {
      if (!authToken) throw new Error("NO_TOKEN");
      return queryFavoritePage({ baseUrl: API_BASE_URL }, authToken, input);
    },
    staleTime: 30_000,
  });
}

/**
 * `POST /favorites/add` — toggle ON a spu (or shop).
 * `authToken` must be a valid JWT; if null, mutation will throw a NO_TOKEN
 * error before fetching so the consumer can show a "Sign in to favorite" toast.
 */
export function useAddFavorite(authToken: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AddFavoritesInput) => {
      if (!authToken) {
        throw new Error("NO_TOKEN");
      }
      return addFavorite({ baseUrl: API_BASE_URL }, authToken, input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["favorites"] });
    },
  });
}

/**
 * `DELETE /favorites/deleteBatch` — toggle OFF one or more spus.
 */
export function useDeleteFavorites(authToken: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: DeleteFavoritesInput) => {
      if (!authToken) {
        throw new Error("NO_TOKEN");
      }
      return deleteFavoritesBatch({ baseUrl: API_BASE_URL }, authToken, input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["favorites"] });
    },
  });
}
